<script setup lang="ts">
import { onMounted, onBeforeUnmount } from 'vue'
import Lenis from '@studio-freight/lenis'
import HeaderView from '@/components/HeaderView.vue'
import FooterView from '@/components/FooterView.vue'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import RelatedLinks from '@/components/RelatedLinks.vue'
import DemoModal from '@/components/DemoModal.vue'
import ProductsDemoSection from '@/components/ProductsDemoSection.vue'
import { useSEO } from '@/composables/useSEO'
import { useDemoModal } from '@/composables/useDemoModal'

const { updateMetaTags } = useSEO()
const { isModalOpen, brevoLink, openModal, closeModal } = useDemoModal()

let lenis: Lenis | null = null

onMounted(() => {
  // SEO optimizado para keyword principal
  updateMetaTags({
    title: 'Software CMMS en Colombia: Guía Completa + Comparativa 2025 | Fourin',
    description:
      'Descubre el mejor software CMMS en Colombia. Guía completa con comparativa 2025, beneficios, precios y cómo elegir. Fourintrack es la solución líder para gestión de mantenimiento en Colombia y LATAM.',
    keywords:
      'software CMMS Colombia, CMMS Colombia, software mantenimiento Colombia, GMAO Colombia, sistema mantenimiento Colombia, CMMS en la nube Colombia, gestión activos Colombia',
    url: 'https://fourin.co/cmms-colombia',
    canonical: 'https://fourin.co/cmms-colombia',
    type: 'website',
    organization: true,
    breadcrumbs: [
      { name: 'Inicio', url: '/' },
      { name: 'Software CMMS en Colombia', url: '/cmms-colombia' },
    ],
  })

  lenis = new Lenis({
    lerp: 0.1,
    smoothWheel: true,
  })
  function raf(time: number) {
    lenis?.raf(time)
    requestAnimationFrame(raf)
  }
  requestAnimationFrame(raf)
  window.scrollTo({ top: 0, behavior: 'smooth' })
})

onBeforeUnmount(() => {
  if (lenis) {
    lenis.destroy()
  }
})

const relatedLinks = [
  {
    title: 'GMAO: Qué es, beneficios y cómo elegir en LATAM',
    url: '/gmao-latinoamerica',
    description: 'Guía completa sobre GMAO en Latinoamérica',
    type: 'page' as const,
  },
  {
    title: 'Fourintrack - Software CMMS/GMAO',
    url: '/products',
    description: 'Conoce nuestro software CMMS en la nube',
    type: 'product' as const,
  },
  {
    title: 'Blog - Artículos sobre mantenimiento',
    url: '/blog',
    description: 'Aprende sobre mantenimiento y gestión de activos',
    type: 'page' as const,
  },
]
</script>

<template>
  <div>
    <HeaderView />
    <DemoModal :show="isModalOpen" :url="brevoLink" @close="closeModal" />

    <main class="cmms-colombia-page">
      <!-- Hero Section -->
      <section class="hero-section">
        <div class="container">
          <Breadcrumbs
            :items="[
              { name: 'Inicio', url: '/' },
              { name: 'Software CMMS en Colombia', url: '/cmms-colombia' },
            ]"
          />
          <div class="hero-content">
            <h1 class="hero-title">Software CMMS en Colombia: Guía Completa 2025</h1>
            <p class="hero-description">
              Descubre cómo elegir el mejor software CMMS para tu empresa en Colombia. Comparativa,
              precios, beneficios y todo lo que necesitas saber sobre sistemas de gestión de
              mantenimiento en 2025.
            </p>
            <button class="cta-button" @click="openModal">Agenda una Demo Gratuita</button>
          </div>
        </div>
      </section>

      <!-- Product Showcase Section -->
      <section class="showcase-section">
        <div class="container">
          <div class="showcase-content">
            <div class="showcase-text">
              <h2>Optimiza la Gestión de Mantenimiento con FourinTrack</h2>
              <p class="showcase-subtitle">
                El software CMMS en la nube más completo para empresas en Colombia. Reduce costos
                hasta un 30%, aumenta la disponibilidad de activos y mejora la eficiencia operativa
                con tecnología de última generación.
              </p>
              <div class="benefits-highlight">
                <div class="benefit-item">
                  <span class="benefit-number">30%</span>
                  <span class="benefit-label">Reducción de costos</span>
                </div>
                <div class="benefit-item">
                  <span class="benefit-number">70%</span>
                  <span class="benefit-label">Menos paradas</span>
                </div>
                <div class="benefit-item">
                  <span class="benefit-number">95%</span>
                  <span class="benefit-label">Cumplimiento</span>
                </div>
              </div>
            </div>
            <div class="showcase-image">
              <img
                src="/src/assets/images/banner.png"
                alt="Dashboard FourinTrack - Software CMMS Colombia"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Features Grid Section -->
      <section class="features-section">
        <div class="container">
          <h2 class="section-title">Características del Mejor Software CMMS en Colombia</h2>
          <p class="section-subtitle">
            FourinTrack incluye todas las funcionalidades que necesitas para optimizar tu gestión de
            mantenimiento y activos
          </p>
          <div class="features-grid">
            <div class="feature-card">
              <img
                src="/src/assets/images/planifica2.png"
                alt="Planifica mantenimientos preventivos"
                class="feature-image"
                loading="lazy"
              />
              <h3>Planifica</h3>
              <p>
                Programa y gestiona mantenimientos preventivos y predictivos de forma automática.
                Optimiza la planificación de recursos y reduce tiempos de inactividad.
              </p>
            </div>
            <div class="feature-card">
              <img
                src="/src/assets/images/optimiza.png"
                alt="Optimiza operaciones de mantenimiento"
                class="feature-image"
                loading="lazy"
              />
              <h3>Optimiza</h3>
              <p>
                Mejora la eficiencia operativa con asignación inteligente de recursos, gestión de
                inventario optimizada y análisis de tendencias en tiempo real.
              </p>
            </div>
            <div class="feature-card">
              <img
                src="/src/assets/images/controla.png"
                alt="Controla activos y mantenimientos"
                class="feature-image"
                loading="lazy"
              />
              <h3>Controla</h3>
              <p>
                Supervisa todos tus activos desde un solo lugar. Controla costos, tiempos de
                respuesta y cumplimiento normativo (RETIE, NTC) de forma automatizada.
              </p>
            </div>
            <div class="feature-card">
              <img
                src="/src/assets/images/analiza.png"
                alt="Analiza datos de mantenimiento"
                class="feature-image"
                loading="lazy"
              />
              <h3>Analiza</h3>
              <p>
                Genera reportes automáticos y dashboards con KPIs clave (MTTR, MTBF, OEE). Toma
                decisiones basadas en datos para mejorar continuamente.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Content Section -->
      <section class="content-section">
        <div class="container">
          <article class="main-content">
            <h2>¿Por qué elegir un Software CMMS en Colombia en 2025?</h2>
            <p>
              La <strong>optimización de activos</strong> y la
              <strong>eficiencia operativa</strong>{" "} son prioridades clave para empresas en
              Colombia. Un <strong>Software CMMS</strong>{" "} (Computerized Maintenance Management
              System) o <strong>GMAO</strong> (Gestión de Mantenimiento Asistida por Ordenador) es
              la solución tecnológica que está transformando la gestión de mantenimiento en el país.
            </p>
            <p>
              Cada vez más empresas colombianas están adoptando
              <strong>software de mantenimiento</strong>{" "} para digitalizar sus operaciones,
              cumplir con normativas locales (RETIE, NTC) y mejorar su competitividad en el mercado.
            </p>

            <h2>Beneficios Comprobados del Software CMMS en Colombia</h2>
            <div class="benefits-grid-text">
              <div class="benefit-box">
                <h3>Reducción de Costos Operativos</h3>
                <p>
                  Reduce hasta un <strong>30% los costos de mantenimiento</strong> mediante la
                  optimización de recursos, prevención de fallas costosas y gestión eficiente de
                  inventario de repuestos.
                </p>
              </div>
              <div class="benefit-box">
                <h3>Menos Paradas No Planificadas</h3>
                <p>
                  Disminuye las paradas imprevistas hasta un <strong>70%</strong> con mantenimiento
                  preventivo programado y detección temprana de problemas mediante sensores IoT.
                </p>
              </div>
              <div class="benefit-box">
                <h3>Mayor Disponibilidad de Activos</h3>
                <p>
                  Aumenta la disponibilidad hasta un <strong>97%</strong> con planificación
                  proactiva, gestión de órdenes de trabajo eficiente y seguimiento en tiempo real
                  del estado de tus activos.
                </p>
              </div>
              <div class="benefit-box">
                <h3>Cumplimiento Normativo Automatizado</h3>
                <p>
                  Cumple con normativas colombianas (RETIE, NTC, ISO) de forma automatizada. Mantén
                  toda la documentación, historial y evidencias organizadas para auditorías.
                </p>
              </div>
              <div class="benefit-box">
                <h3>Gestión de Inventario Optimizada</h3>
                <p>
                  Reduce el inventario de repuestos hasta un <strong>25%</strong> evitando
                  sobrestock y faltantes. Solicita solo lo necesario antes de cada intervención.
                </p>
              </div>
              <div class="benefit-box">
                <h3>Reportes e Indicadores en Tiempo Real</h3>
                <p>
                  Genera reportes automáticos de KPIs como MTTR, MTBF, OEE y disponibilidad.
                  Dashboards interactivos para toma de decisiones informadas.
                </p>
              </div>
            </div>

            <h2>Características del Mejor Software CMMS en la Nube para Colombia</h2>
            <p>
              FourinTrack es un <strong>software CMMS en la nube</strong> desarrollado
              específicamente para empresas en Colombia y Latinoamérica. Incluye todas las
              funcionalidades modernas que necesitas:
            </p>
            <div class="features-list">
              <div class="feature-item">
                <strong>Gestión de Órdenes de Trabajo:</strong> Crea, asigna y da seguimiento a
                órdenes de trabajo correctivas, preventivas y predictivas desde cualquier
                dispositivo.
              </div>
              <div class="feature-item">
                <strong>Registro y Seguimiento de Activos:</strong> Mantén un registro completo de
                todos tus activos con historial, documentación y geolocalización en tiempo real.
              </div>
              <div class="feature-item">
                <strong>Planificación de Mantenimiento Preventivo:</strong> Programa automáticamente
                mantenimientos basados en tiempo, uso o condiciones de los activos.
              </div>
              <div class="feature-item">
                <strong>Gestión de Inventario:</strong> Controla repuestos y materiales con alertas
                de stock mínimo, solicitudes automáticas y trazabilidad completa.
              </div>
              <div class="feature-item">
                <strong>Acceso Móvil:</strong> Aplicaciones para Android e iOS que permiten a tus
                técnicos trabajar en campo sin conexión y sincronizar cuando vuelvan a tener
                internet.
              </div>
              <div class="feature-item">
                <strong>Integración IoT:</strong> Conecta sensores inteligentes para mantenimiento
                predictivo y monitoreo en tiempo real de condiciones de activos.
              </div>
              <div class="feature-item">
                <strong>Geolocalización:</strong> Ubica tus activos en tiempo real y optimiza rutas
                de mantenimiento para reducir tiempos de desplazamiento.
              </div>
              <div class="feature-item">
                <strong>Reportes y Dashboards:</strong> Visualiza KPIs clave, genera reportes
                automáticos y toma decisiones basadas en datos históricos y en tiempo real.
              </div>
            </div>

            <h2>FourinTrack: El Software CMMS Líder en Colombia 2025</h2>
            <p>
              <strong>FourinTrack</strong> es la solución de
              <strong>gestión de mantenimiento</strong>{" "} más completa para empresas en Colombia.
              Desarrollado específicamente para el mercado latinoamericano, ofrece todas las
              funcionalidades necesarias para optimizar tu operación de mantenimiento.
            </p>

            <div class="product-showcase">
              <div class="product-image">
                <img
                  src="/src/assets/images/fourintrackdashboard.webp"
                  alt="Dashboard FourinTrack - Software CMMS Colombia"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div class="product-benefits">
                <h3>¿Por qué FourinTrack es el mejor CMMS para Colombia?</h3>
                <ul>
                  <li>
                    <strong>100% en la nube:</strong> Acceso desde cualquier dispositivo, sin
                    instalaciones complicadas ni servidores propios
                  </li>
                  <li>
                    <strong>Interfaz intuitiva en español:</strong> Diseñada para ser fácil de usar
                    por todo tu equipo técnico
                  </li>
                  <li>
                    <strong>Soporte técnico local:</strong> Equipo en Colombia disponible en horario
                    laboral colombiano
                  </li>
                  <li>
                    <strong>Tecnología de última generación:</strong> Integración con sensores IoT,
                    geolocalización y análisis predictivo
                  </li>
                  <li>
                    <strong>Precios competitivos:</strong> Adaptados al mercado colombiano con
                    planes flexibles
                  </li>
                  <li>
                    <strong>Prueba gratis 14 días:</strong> Sin tarjeta de crédito, sin compromiso.
                    Prueba todas las funcionalidades antes de decidir
                  </li>
                  <li>
                    <strong>Implementación rápida:</strong> Tu equipo estará operando en menos de
                    una semana
                  </li>
                  <li>
                    <strong>Escalable:</strong> Crece con tu empresa, desde pequeñas operaciones
                    hasta grandes flotas
                  </li>
                </ul>
              </div>
            </div>

            <h2>Comparativa: ¿Qué buscar en un Software CMMS en Colombia?</h2>
            <p>
              Al evaluar opciones de <strong>software de mantenimiento</strong> para tu empresa en
              Colombia, considera estos factores clave:
            </p>
            <div class="comparison-grid">
              <div class="comparison-item">
                <h4>Precio y Modelo de Suscripción</h4>
                <p>
                  FourinTrack ofrece planes mensuales flexibles adaptados al mercado colombiano, sin
                  costos ocultos ni licencias perpetuas costosas.
                </p>
              </div>
              <div class="comparison-item">
                <h4>Implementación Rápida</h4>
                <p>
                  Nuestro equipo te ayuda a configurar el sistema en menos de una semana. Sin
                  complicaciones técnicas ni largos períodos de implementación.
                </p>
              </div>
              <div class="comparison-item">
                <h4>Soporte Local en Español</h4>
                <p>
                  Soporte técnico en Colombia, en español, disponible en horario laboral colombiano.
                  Respuesta rápida a tus consultas y problemas.
                </p>
              </div>
              <div class="comparison-item">
                <h4>Escalabilidad</h4>
                <p>
                  El software crece con tu empresa. Desde pequeñas operaciones hasta grandes flotas
                  de activos, FourinTrack se adapta a tus necesidades.
                </p>
              </div>
              <div class="comparison-item">
                <h4>Integraciones</h4>
                <p>
                  Compatible con sistemas ERP, contabilidad y otros software empresariales. APIs
                  disponibles para integraciones personalizadas.
                </p>
              </div>
              <div class="comparison-item">
                <h4>Aplicaciones Móviles</h4>
                <p>
                  Apps nativas para Android e iOS que permiten a tus técnicos trabajar en campo,
                  incluso sin conexión a internet.
                </p>
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- Demo Section -->
      <ProductsDemoSection />

      <!-- Related Links -->
      <section class="related-section">
        <div class="container">
          <RelatedLinks :links="relatedLinks" title="Contenido Relacionado" />
        </div>
      </section>
    </main>

    <FooterView />
  </div>
</template>

<style scoped>
main {
  margin-top: 90px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-section {
  padding: 100px 0 80px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.hero-content {
  max-width: 800px;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  line-height: 1.2;
}

.hero-description {
  font-size: 1.25rem;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 32px;
}

.cta-button {
  padding: 16px 32px;
  background: linear-gradient(45deg, #1297ef, #4ab7ff);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(18, 151, 239, 0.3);
}

.content-section {
  padding: 80px 0;
  background: white;
}

.main-content {
  max-width: 1200px;
  margin: 0 auto;
}

.main-content h2 {
  font-size: 2rem;
  font-weight: 600;
  color: #1e293b;
  margin: 60px 0 24px;
  line-height: 1.3;
}

.main-content h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 40px 0 20px;
}

.main-content p {
  font-size: 1.125rem;
  line-height: 1.8;
  color: #374151;
  margin-bottom: 24px;
}

.main-content ul {
  margin: 24px 0;
  padding-left: 24px;
}

.main-content li {
  font-size: 1.125rem;
  line-height: 1.8;
  color: #374151;
  margin-bottom: 12px;
}

.main-content strong {
  font-weight: 600;
  color: #1e293b;
}

.related-section {
  padding: 30px 0;
}

/* Showcase Section */
.showcase-section {
  padding: 80px 0;
  background: white;
}

.showcase-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.showcase-text h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  line-height: 1.2;
}

.showcase-subtitle {
  font-size: 1.25rem;
  line-height: 1.8;
  color: #64748b;
  margin-bottom: 40px;
}

.benefits-highlight {
  display: flex;
  gap: 40px;
  margin-top: 32px;
}

.benefit-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.benefit-number {
  font-size: 3rem;
  font-weight: 800;
  color: #1297ef;
  line-height: 1;
  margin-bottom: 8px;
}

.benefit-label {
  font-size: 0.875rem;
  color: #64748b;
  text-align: center;
  font-weight: 500;
}

.showcase-image {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
}

.showcase-image img {
  width: 100%;
  height: auto;
  display: block;
}

/* Features Section */
.features-section {
  padding: 80px 0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 1.125rem;
  color: #64748b;
  text-align: center;
  max-width: 700px;
  margin: 0 auto 60px;
  line-height: 1.6;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
}

.feature-card {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(18, 151, 239, 0.15);
  border-color: #1297ef;
}

.feature-card .feature-image {
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-bottom: 20px;
}

.feature-card h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
}

.feature-card p {
  font-size: 1rem;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
}

/* Benefits Grid Text */
.benefits-grid-text {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin: 40px 0;
}

.benefit-box {
  background: linear-gradient(135deg, rgba(18, 151, 239, 0.05) 0%, rgba(74, 183, 255, 0.05) 100%);
  border-radius: 12px;
  padding: 30px;
  border: 1px solid rgba(18, 151, 239, 0.1);
}

.benefit-box h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
}

.benefit-box p {
  font-size: 1rem;
  line-height: 1.7;
  color: #374151;
  margin: 0;
}

/* Features List */
.features-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 30px 0;
}

.feature-item {
  padding: 20px;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 4px solid #1297ef;
  font-size: 1rem;
  line-height: 1.6;
  color: #374151;
}

.feature-item strong {
  color: #1e293b;
  font-weight: 600;
}

/* Product Showcase */
.product-showcase {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin: 60px 0;
  padding: 40px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.product-image {
  border-radius: 12px;
  overflow: hidden;
}

.product-image img {
  width: 100%;
  height: auto;
  display: block;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
}

.product-benefits h3 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 24px;
}

.product-benefits ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.product-benefits li {
  padding: 16px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 1rem;
  line-height: 1.7;
  color: #374151;
}

.product-benefits li:last-child {
  border-bottom: none;
}

.product-benefits li strong {
  color: #1297ef;
  font-weight: 600;
}

/* Comparison Grid */
.comparison-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin: 40px 0;
}

.comparison-item {
  background: white;
  border-radius: 12px;
  padding: 30px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.comparison-item:hover {
  border-color: #1297ef;
  box-shadow: 0 8px 25px rgba(18, 151, 239, 0.1);
}

.comparison-item h4 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
}

.comparison-item p {
  font-size: 1rem;
  line-height: 1.7;
  color: #64748b;
  margin: 0;
}

@media (max-width: 1024px) {
  .showcase-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .showcase-image {
    order: -1;
  }

  .product-showcase {
    grid-template-columns: 1fr;
  }

  .features-grid,
  .benefits-grid-text,
  .features-list,
  .comparison-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-description {
    font-size: 1.125rem;
  }

  .main-content h2 {
    font-size: 1.5rem;
  }

  .main-content p,
  .main-content li {
    font-size: 1rem;
  }

  .showcase-section,
  .features-section {
    padding: 60px 0;
  }

  .showcase-text h2 {
    font-size: 2rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .benefits-highlight {
    flex-direction: column;
    gap: 30px;
  }

  .benefit-number {
    font-size: 2.5rem;
  }

  .product-showcase {
    padding: 30px 20px;
  }

  .feature-card,
  .benefit-box,
  .comparison-item {
    padding: 20px;
  }
}
</style>
